import "../css/ClusterListings.css";
import { Box, Checkbox, CircularProgress, Grid, Paper } from "@mui/material";
import { Helmet } from "react-helmet";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { BootstrapOutlineButton } from "components/buttons/CustomButtons";
import { Link, useSearchParams } from "react-router-dom";
import {
  CustomerEnvironment,
  EnvironmentTypeOption,
  environmentTypeOptions as environmentTypes,
  ProvisionableSku,
  ProvisionRequest,
  VpcInfoResponse,
  VpcRouteTable,
  VpcSubnet,
} from "utils/saastypes";
import LinearProgress from "../components/LinearProgress";
import {
  IdTitle,
  useActionWithPath,
  useCustomerRegions,
  useCustomerVPCIds,
  useProvisionableSkus,
} from "utils/query";
import {
  clusterNameExp,
  getComputeUnitIcon,
  getEnvironmentIcon,
  getValueForAutocomplete,
} from "utils/utils";
import ClusterField from "../components/ClusterField";
import AlertDialog from "../components/AlertDialog";
import StyledMainButton from "../components/buttons/StyledMainButton";
import { useNavigate } from "react-router";
import { SUB_DOMAIN_REGEX } from "constants/common";
import { useAuth } from "auth/AuthContext";
import { useAuthMachine } from "auth/state/hook";

function getInitialErrors(provisionRequest: ProvisionRequest) {
  const newErrors: any = {};
  if (
    undefined === provisionRequest.customerAccountId ||
    provisionRequest.customerAccountId?.trim() === ""
  ) {
    newErrors["customerAccountId"] = "Cloud provider account id cannot be null";
  }
  if (
    undefined === provisionRequest.clusterName ||
    !clusterNameExp.test(provisionRequest.clusterName)
  ) {
    newErrors["clusterName"] = "Cluster name is not valid";
  }
  if (
    undefined === provisionRequest.region ||
    provisionRequest.region?.trim() === ""
  ) {
    newErrors["region"] = "Region cannot be null";
  }
  return newErrors;
}

const validateDataForCft = (
  provisionRequest: ProvisionRequest,
  errors: any,
  setErrors: any
): boolean => {
  const newErrors = getInitialErrors(provisionRequest);
  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};

const extendedValidation = (
  provisionRequest: ProvisionRequest,
  errors: any,
  setErrors: any,
  selectedDomain: any,
  sizingMode: "legacy" | "sku",
  selectedSku: SkuOption | null
): boolean => {
  const newErrors = getInitialErrors(provisionRequest);
  if (undefined === provisionRequest.vpcId || null === provisionRequest.vpcId) {
    newErrors["vpcId"] = "Invalid VPC id";
  }
  // Guard against submitting in SKU mode with nothing selected, which would otherwise fall
  // through and provision using the legacy computeUnits still held in state.
  if (sizingMode === "sku" && !selectedSku) {
    newErrors["skuName"] = "A SKU is required";
  }
  if (selectedSku) {
    // A SKU is sized for an exact number of AZs: the terraform filler derives the real AZ
    // count from azIds.size() and scales the Redis node count off it, so anything other than
    // exactly azCount silently mis-sizes the cluster.
    const selectedAzCount = provisionRequest.azIds?.length ?? 0;
    if (selectedAzCount !== selectedSku.azCount) {
      newErrors[
        "azIds"
      ] = `${selectedSku.skuName} requires exactly ${selectedSku.azCount} availability zones (${selectedAzCount} selected)`;
    }
  } else if (
    undefined === provisionRequest.azIds ||
    provisionRequest.azIds?.length === 0 ||
    provisionRequest.azIds?.length > 3
  ) {
    newErrors["azIds"] = "Invalid availability zone ids";
  }
  if (
    (!provisionRequest.subDomain || !provisionRequest.domainUrl) &&
    !selectedDomain?.fixedDomain
  ) {
    newErrors["subDomain"] = "Sub domain is required";
  } else if (!SUB_DOMAIN_REGEX.test(provisionRequest.subDomain)) {
    newErrors["subDomain"] =
      "Invalid sub domain. Allowed [a-z0-9] separated by hyphens";
  }

  if (
    undefined === provisionRequest.domainUrl ||
    provisionRequest.domainUrl?.length <= 0
  ) {
    newErrors["domainUrl"] = "Domain URL is required";
  }
  if (
    undefined === provisionRequest.ingressType ||
    null === provisionRequest.ingressType
  ) {
    newErrors["ingressType"] = "Ingress Type is required";
  }
  if (
    undefined === provisionRequest.computeUnits ||
    null === provisionRequest.computeUnits
  ) {
    newErrors["computeUnits"] = "Compute units is required";
  }
  if (
    undefined === provisionRequest.environmentType ||
    null === provisionRequest.environmentType
  ) {
    newErrors["environmentType"] = "The Environment Type is required";
  }
  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};

const ingressTypes = [
  { id: "INTERNAL", title: "VPC Only - Access via VPN or Peering" },
  { id: "EXTERNAL", title: "Internet - Access using Credentials" },
];

const computeUnits = [
  { id: "1", title: "Small - 100s - 1000s of Workflows / Day" },
  { id: "2", title: "Medium - 1000s - 10,000s of Workflows / Day" },
  { id: "3", title: "Large - 100,000s - 1,000,000s of Workflows / Day" },
  { id: "4", title: "Extra Large - > 1,000,000s of Workflows / Day" },
  {
    id: "10",
    title: "Optimized for High Performance",
  },
];

const authenticationTypes = [
  { id: "true", title: "Yes" },
  { id: "false", title: "No" },
];

type Route53Option = {
  id: string;
  title: string;
  fixedDomain: boolean;
};

// A provisionable SKU rendered as a dropdown option: `id` is the SkuName sent to the backend,
// `title` is the display label, and the SKU's own fields (notably azCount) ride along.
type SkuOption = ProvisionableSku & { id: string };

export default function CreateNewEnvironmentCustomClusterNextStep() {
  const { authService } = useAuth();
  const [{ isOrkesUser }] = useAuthMachine(authService);

  const [searchParams] = useSearchParams();
  const [provisionRequest, setProvisionRequest] = useState<ProvisionRequest>(
    new ProvisionRequest(
      searchParams.get("cloudProvider") as string,
      searchParams.get("accountId") as string,
      searchParams.get("clusterName") as string,
      1,
      environmentTypes[0].id // e.g. PRODUCTION
    )
  );
  const [openConfirmCreate, setOpenConfirmCreate] = useState<boolean>(false);
  const [errors, setErrors] = useState<any>({});
  const [vpcInfo, setVpcInfo] = useState<VpcInfoResponse | null>(null);
  const [vpcValidReason, setVpcValidReason] = useState<string>("");
  const [azCount, setAzCount] = useState<number>(0);
  const [isAzFocused, setIsAzFocused] = useState<boolean>(false);

  const [ignoreValidationChk, setIgnoreValidationChk] =
    useState<boolean>(false);
  // Whether the customer owns the networking. Chosen on the previous page, because it decides
  // which CloudFormation template is generated. Read back from the server rather than carried
  // in the URL: the stack row records what the customer was actually given, and a query
  // parameter could be edited to disagree with it.
  const [byon, setByon] = useState<boolean>(false);
  const [createErrorReason, setCreateErrorReason] = useState<string>("");
  const [subDomain, setSubDomain] = useState<string>("");
  const [selectedDomain, setSelectedDomain] = useState<any>(null);
  const [environmentType, setEnvironmentType] = useState<EnvironmentTypeOption>(
    environmentTypes[0] // keep it in sync with environmentTypeOptions[0].id up above!
  );
  const [subnetSelected, setSubnetsSelected] = useState<
    { id: string; title: string }[]
  >([]);
  const [enableApiGateway, setEnableApiGateway] = useState(false);

  // Sizing mode. Legacy is the default, so nothing changes until an @orkes.io user opts in.
  // The SKU fleet carries AWS infra columns only and has no workflow mapped for other
  // providers, so it is offered on AWS exclusively.
  const [sizingMode, setSizingMode] = useState<"legacy" | "sku">("legacy");
  // Undefined means no choice yet; null means the user cleared the selection.
  const [selectedSku, setSelectedSku] = useState<SkuOption | null>();
  const skuModeAvailable =
    isOrkesUser && provisionRequest.cloudProvider === "AWS";
  const { data: provisionableSkus } = useProvisionableSkus(skuModeAvailable);

  // The dropdown option carries the SKU's own fields (as Route53Option does with fixedDomain),
  // so selecting one yields azCount directly instead of looking the SKU up again by name.
  const skuItems: SkuOption[] = useMemo(
    () =>
      (provisionableSkus ?? []).map((sku) => ({
        ...sku,
        id: sku.skuName,
        title: `${sku.title} - ${sku.azCount} availability zones`,
      })),
    [provisionableSkus]
  );

  const defaultedSku =
    selectedSku === undefined ? skuItems[0] ?? null : selectedSku;
  const activeSku = sizingMode === "sku" ? defaultedSku : null;

  useEffect(() => {
    // this will sync the provRequest with the selected env
    setProvisionRequest((prevState) => ({
      ...prevState,
      environmentType: environmentType?.id,
    }));
  }, [environmentType]);

  const handleChangeIngnoreValidation = (checked: boolean) => {
    setIgnoreValidationChk(checked);
    if (checked && vpcValidReason.length > 0) {
      setVpcValidReason("");
      validateVpc(provisionRequest.vpcId, checked);
    } else {
      setVpcInfo(null);
      setSubnetsSelected([]);
    }
  };

  const environmentTypeOptions = useMemo(() => {
    const result = [...environmentTypes];

    if (!isOrkesUser) {
      result.pop();
    }

    return result;
  }, [isOrkesUser]);

  const availabilityZones = useMemo(() => {
    if (vpcInfo) {
      return vpcInfo.availabilityZones.map((zone) => ({
        id: zone,
        title: zone,
      }));
    }
  }, [vpcInfo]);

  // Controlled value for the AZ picker. Derived by mapping over azIds (not by filtering the
  // options) so the user's click order is preserved — that order reaches terraform as
  // preferred_cache_cluster_azs. Returns the same option object references as `items`, so MUI's
  // default identity comparison matches without needing isOptionEqualToValue.
  const selectedAzOptions = useMemo(
    () =>
      (provisionRequest.azIds ?? [])
        .map((azId) => availabilityZones?.find((zone) => zone.id === azId))
        .filter((zone): zone is IdTitle => zone !== undefined),
    [provisionRequest.azIds, availabilityZones]
  );

  const route53Zones = useMemo(() => {
    if (vpcInfo) {
      return vpcInfo.route53?.reduce((result: Route53Option[], zone: any) => {
        let dnWoDot = zone.domainName;

        if (dnWoDot.endsWith(".")) {
          dnWoDot = zone.domainName.substring(0, zone.domainName.length - 1); // Remove the "." at the end
        }

        result.push({
          id: zone.domainName,
          title: `${dnWoDot} - ${zone.fixedDomain ? "Orkes" : "Customer"}`,
          fixedDomain: zone.fixedDomain,
        });

        return result;
      }, []);
    }

    return [];
  }, [vpcInfo]);

  const { data: regions, isFetching: regionsFetching } = useCustomerRegions(
    provisionRequest.clusterName,
    provisionRequest.customerAccountId
  );
  const { data: vpcIds, isFetching: vpcIdsFetching } = useCustomerVPCIds(
    provisionRequest.clusterName,
    provisionRequest.customerAccountId,
    provisionRequest.region
  );

  const handleChangeData = (
    e: any,
    value?: any,
    reason?: string,
    id?: string,
    domain?: string
  ) => {
    if (id) {
      const additionalValues: any = {};
      if (id === "region") {
        additionalValues["vpcId"] = null;
        additionalValues["prevVpcId"] = provisionRequest["vpcId"];
        setVpcInfo(null);
        setVpcValidReason("");
      }
      if (id === "vpcId") {
        additionalValues["prevVpcId"] = provisionRequest["vpcId"];
        setVpcInfo(null);

        if (additionalValues["prevVpcId"] !== value) {
          setVpcValidReason("");
        }
      }
      if (id === "enableAuthentication") {
        additionalValues["enableAuthentication"] = value === "true";
      }

      let newPR = {
        ...provisionRequest,
        ...additionalValues,
        ...(domain ? { domainUrl: domain } : {}),
        [id]: value,
      };
      setProvisionRequest(newPR);
      if (id === "azIds") {
        if (Array.isArray(value)) {
          if (value.length > (activeSku?.azCount ?? 3)) {
            setErrors((prevErrors: any) => ({
              ...prevErrors,
              azIds: "Too many AZs",
            }));
          } else if (errors["azIds"] !== undefined) {
            setErrors((prevErrors: any) => ({
              ...prevErrors,
              azIds: undefined,
            }));
          }
          setAzCount(value.length);
        }
      } else {
        if (value !== null && value !== undefined && errors[id] !== undefined) {
          setErrors((prevErrors: any) => ({
            ...prevErrors,
            [id]: undefined,
          }));
        }
      }
    } else {
      setProvisionRequest({
        ...provisionRequest,
        [e.target.name]: e.target.value,
        ...(domain ? { domainUrl: domain } : {}),
      });
    }
  };

  // Switching sizing mode never mutates the AZ selection: silently dropping an AZ the user
  // deliberately picked is the same class of invisible mis-sizing we are guarding against, and
  // azIds order feeds preferred_cache_cluster_azs. An over-selection is reported instead.
  const handleChangeSizingMode = (mode: "legacy" | "sku") => {
    setSizingMode(mode);
    setErrors((prevErrors: any) => ({ ...prevErrors, azIds: undefined }));
  };

  const handleChangeSku = (sku: SkuOption | null) => {
    setSelectedSku(sku);
    setErrors((prevErrors: any) => ({
      ...prevErrors,
      azIds: undefined,
      computeUnits: undefined,
    }));
  };

  // TODO this is messy. and it will become a bug report for sure. Sorry.
  useEffect(() => {
    if (
      provisionRequest.prevVpcId !== provisionRequest.vpcId &&
      vpcInfo === null &&
      provisionRequest.vpcId !== null
    ) {
      validateVpc(provisionRequest.vpcId, ignoreValidationChk);
    }
    // eslint-disable-next-line
  }, [provisionRequest, vpcInfo, ignoreValidationChk]);

  const commonErrorText =
    "Please use a newly created or empty VPC with a private IP CIDR block from /16 to /23.";
  const validateVpcAction = useActionWithPath({
    onSuccess: (data: VpcInfoResponse) => {
      setVpcInfo(data);
      if (data.vpcCIDR) {
        try {
          let str = data.vpcCIDR;
          let mask = parseInt(str.substring(str.lastIndexOf("/") + 1));
          if (mask < 16 || mask > 23) {
            setVpcValidReason(
              `Require a CIDR mask from 16 to 23, VPC has a CIDR mask of ${mask} - CIDR - ${data.vpcCIDR}. ${commonErrorText}`
            );
            return;
          } else {
            setProvisionRequest({
              ...provisionRequest,
            });
          }
        } catch (e) {
          setVpcValidReason(`Unable to parse CIDR info. ${commonErrorText}`);
          return;
        }
      } else {
        setVpcValidReason(`Unable to read CIDR info. ${commonErrorText}`);
        return;
      }
      setVpcValidReason("");
      console.log("VPC info fetched");
    },
    onError: async (response: any) => {
      response.json().then((json: any) => {
        setVpcValidReason(`${json.message}.`);
      });
      console.error("VPC error: ", response);
    },
  });
  const navigate = useNavigate();

  const provisionEnvironmentAction = useActionWithPath({
    onSuccess: (data: CustomerEnvironment) => {
      setTimeout(() => {
        navigate(
          `/home/clusterprogess?clusterName=${data.name}&showSucess=true`
        );
      }, 3000);
    },
    // Without this the server's rejection is swallowed: the spinner stops and nothing appears,
    // so a VPC the server refuses looks like a button that does not work.
    onError: async (response: any) => {
      try {
        const json = await response.json();
        setCreateErrorReason(json.message ?? "Unable to create the cluster.");
      } catch {
        setCreateErrorReason("Unable to create the cluster.");
      }
    },
  });

  const cftStackAction = useActionWithPath({
    onSuccess: (data: any) => setByon(data?.byon === true),
  });

  useEffect(() => {
    if (
      provisionRequest.cloudProvider === "AWS" &&
      provisionRequest.clusterName
    ) {
      // @ts-ignore
      cftStackAction.mutate({
        method: "get",
        path: `/generateOrkesRoleCft?clusterName=${provisionRequest.clusterName.toLowerCase()}`,
      });
    }
    // eslint-disable-next-line
  }, [provisionRequest.clusterName, provisionRequest.cloudProvider]);

  const subnetItems = useMemo(() => {
    return (
      vpcInfo?.vpcSubnets.map(({ subnetID, nameTagValue }) => ({
        id: subnetID,
        title: nameTagValue ?? subnetID,
      })) ?? []
    );
  }, [vpcInfo]);

  /**
   * The subnets Orkes can actually use: explicitly associated with a route table that has a
   * default route which carries traffic. That is the same test the server applies, so a legacy
   * VPC defaults to exactly what the wizard used to submit on its behalf, and a transit gateway
   * VPC leaves out the attachment subnets without the customer having to know to.
   */
  const routableSubnetItems = useMemo(() => {
    if (!vpcInfo) return [];
    const routable = new Set<string>();
    vpcInfo.routeTables?.forEach((routeTable) => {
      const carriesDefaultRoute = routeTable.routes?.some((route) => {
        const state = (route.state ?? "active").toLowerCase();
        return (
          route.destinationCIDR === "0.0.0.0/0" &&
          !!route.gateway &&
          state !== "blackhole" &&
          state !== "filtered"
        );
      });
      if (carriesDefaultRoute) {
        routeTable.subnetAssociations?.forEach((subnetId) => {
          // The main route table reports an association with no subnet id at all.
          if (subnetId) routable.add(subnetId);
        });
      }
    });
    return subnetItems.filter(({ id }) => routable.has(id));
  }, [vpcInfo, subnetItems]);

  useEffect(() => {
    setSubnetsSelected(routableSubnetItems);
    setCreateErrorReason("");
  }, [routableSubnetItems]);

  /**
   * External ingress needs an internet-facing load balancer, which AWS will not create unless
   * some subnet routes 0.0.0.0/0 to an internet gateway. The same test the server applies, and
   * for the same reason it is not conditioned on customer-owned networking: a customer can
   * perfectly well bring their own VPC that has an internet gateway.
   *
   * Only applied to a VPC that already has subnets. An empty VPC is the path where Orkes builds
   * the networking and creates the gateway itself, so the absence of one proves nothing yet.
   */
  const ingressTypeItems = useMemo(() => {
    const vpcHasSubnets = (vpcInfo?.vpcSubnets?.length ?? 0) > 0;
    if (!vpcInfo || !vpcHasSubnets) return ingressTypes;

    const hasInternetGatewayRoute = vpcInfo.routeTables?.some((routeTable) =>
      routeTable.routes?.some((route) => {
        const state = (route.state ?? "active").toLowerCase();
        return (
          route.destinationCIDR === "0.0.0.0/0" &&
          !!route.gateway &&
          route.gateway.startsWith("igw") &&
          state !== "blackhole" &&
          state !== "filtered"
        );
      })
    );

    return hasInternetGatewayRoute
      ? ingressTypes
      : ingressTypes.filter(({ id }) => id !== "EXTERNAL");
  }, [vpcInfo]);

  // Changing the VPC can withdraw an option the customer already chose. Left alone, the field
  // keeps showing a value that is no longer in its list and submits it anyway, to be rejected
  // by the server at the last step.
  useEffect(() => {
    if (
      provisionRequest.ingressType &&
      !ingressTypeItems.some(({ id }) => id === provisionRequest.ingressType)
    ) {
      setProvisionRequest((prevState) => ({
        ...prevState,
        ingressType: undefined,
      }));
    }
    // eslint-disable-next-line
  }, [ingressTypeItems]);

  function triggerProvisioningFlow(confirmed: boolean) {
    if (
      extendedValidation(
        provisionRequest,
        errors,
        setErrors,
        selectedDomain,
        sizingMode,
        activeSku
      ) &&
      vpcValidReason === ""
    ) {
      if (confirmed) {
        setOpenConfirmCreate(false);
        // We need to enhance the form to include a sub domain name if required.
        const clusNameLc = provisionRequest.clusterName?.toLocaleLowerCase();

        const subnetsAsIds = subnetSelected.map(({ id }) => id);
        // The selection is always what gets submitted, not just when validation is being
        // skipped. It defaults to every subnet Orkes can use, so a legacy VPC sends what it
        // always sent, and a transit gateway VPC leaves out the attachment subnets.
        const subnetsR: VpcSubnet[] | undefined = vpcInfo?.vpcSubnets.filter(
          ({ subnetID }) => subnetsAsIds.includes(subnetID)
        );
        // Route tables are not a separate choice: every subnet has exactly one, so the server
        // derives them from the subnet selection. Sending the VPC's whole set means a subnet
        // can never appear unassociated just because its route table was left unticked.
        const routeTablesR: VpcRouteTable[] | undefined = vpcInfo?.routeTables;

        const newProvReq = {
          ...provisionRequest,
          // Sizing is resolved either by skuName or by computeUnits, never both. Stripping
          // computeUnits here (rather than clearing it in state) keeps the legacy path and its
          // validation byte-for-byte unchanged. JSON.stringify drops undefined keys.
          ...(activeSku
            ? { skuName: activeSku.skuName, computeUnits: undefined }
            : { skuName: undefined }),
          clusterName: clusNameLc,
          ignoreValidation: ignoreValidationChk,
          domainUrl: `${provisionRequest.domainUrl}`,
          conductorUIURL: `${provisionRequest.domainUrl}`,
          vpcCidr: vpcInfo?.vpcCIDR,
          subnets: subnetsR,
          routeTables: routeTablesR,
          rdsEnabled: true,
          enableApiGateway: enableApiGateway,
        };
        // @ts-ignore
        provisionEnvironmentAction.mutate({
          method: "post",
          path: `/provisionEnvironment`,
          body: JSON.stringify(newProvReq),
          successMessage: `Cluster ${clusNameLc} has been scheduled for provisioning. Redirecting you to monitor provisioning.`,
        });
      } else {
        setOpenConfirmCreate(true);
      }
    }
  }

  function validateVpc(
    vpcId: string | null | undefined,
    ignoreValidation: boolean
  ) {
    if (validateDataForCft(provisionRequest, errors, setErrors) && vpcId) {
      const bodyObj = {
        clusterName: provisionRequest.clusterName,
        customerAccountId: provisionRequest.customerAccountId,
        vpcId: vpcId,
        region: provisionRequest.region,
      };
      // @ts-ignore
      validateVpcAction.mutate({
        method: "post",
        path: `/vpcInfo?ignoreValidation=${ignoreValidation}`,
        body: JSON.stringify(bodyObj),
      });
    }
  }

  const onChangeSubDomain = (e: any, reason: any) => {
    const { value } = e.target;
    setSubDomain(value);
    if (value && selectedDomain && !selectedDomain.fixedDomain) {
      let slicedValue = `${value}.${selectedDomain?.id?.slice(
        0,
        selectedDomain?.id?.length
      )}`;
      setProvisionRequest((prevState) => ({
        ...prevState,
        domainUrl: slicedValue,
      }));
      handleChangeData(e, value, reason, "subDomain", slicedValue);
    } else {
      handleChangeData(e, value, reason, "subDomain");
    }
  };

  const onChangeBaseDomain = (e: any, value: any, reason: any) => {
    let validValue = getValueForAutocomplete(value);

    if (value?.fixedDomain) {
      setSubDomain("");
    } else if (subDomain) {
      validValue = subDomain + "." + validValue?.slice(0, validValue?.length);
    }
    setSelectedDomain(value);
    handleChangeData(e, validValue, reason, "domainUrl");
  };

  return (
    <>
      <Helmet>
        <title>Orkes Cloud - Create New Customer Cluster</title>
      </Helmet>
      {(regionsFetching ||
        vpcIdsFetching ||
        validateVpcAction.isLoading ||
        provisionEnvironmentAction.isLoading) && <LinearProgress />}
      <AlertDialog
        textTitle={""}
        textContent={
          "Please confirm if you want to proceed to provision a cluster based on the selected configuration."
        }
        open={openConfirmCreate}
        setOpen={setOpenConfirmCreate}
        handleConfirm={() => triggerProvisioningFlow(true)}
      />
      <Box
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexDirection: "row",
          borderBottom: "1px solid rgb(200,200,200, 0.5)",
        }}
      >
        <Box
          style={{
            display: "flex",
            justifyContent: "left",
            flexDirection: "column",
            paddingLeft: 25,
            paddingRight: 0,
            paddingTop: 5,
          }}
        >
          <h2 style={{ marginBottom: 15 }}>
            Create Customer Cluster {searchParams.get("clusterName")} in Account{" "}
            {searchParams.get("accountId")}
          </h2>
        </Box>
        <Box
          style={{
            display: "flex",
            justifyContent: "left",
            flexDirection: "row",
            paddingRight: 20,
            paddingTop: 5,
          }}
        >
          <BootstrapOutlineButton
            color={"inherit"}
            style={{
              width: 100,
              fontSize: 15,
              lineHeight: "27px",
              marginRight: 10,
              fontFamily: "Lexend, sans-serif",
              fontWeight: 800,
              textAlign: "center",
              borderRadius: 5,
            }}
            //@ts-ignore
            component={Link}
            to={"/home/clusters"}
          >
            Cancel
          </BootstrapOutlineButton>
        </Box>
      </Box>

      <Box
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignContent: "center",
          flexDirection: "column",
          width: "100%",
          minHeight: "calc(100vh - 137px)",
          maxHeight: "calc(100vh - 137px)",
          overflowY: "auto",
        }}
      >
        <Paper
          style={{
            marginTop: 30,
            marginLeft: 30,
            marginRight: 30,
            padding: 20,
            fontFamily: "Lexend, sans-serif",
            minWidth: 400,
            maxWidth: 900,
          }}
        >
          <p
            style={{
              fontSize: 18,
              marginTop: 0,
              fontFamily: "Lexend, sans-serif",
              fontWeight: 600,
            }}
          >
            Customer Cluster in your Cloud Account
          </p>
          <Grid container spacing={2} sx={{ width: 600 }}>
            <ClusterField
              id={"cloudProvider"}
              label={"Cloud Provider"}
              labelValue={provisionRequest.cloudProvider}
              type={"label"}
            />
            <ClusterField
              id={"customerAccountId"}
              label={"Account Id"}
              type={"label"}
              labelValue={provisionRequest.customerAccountId}
            />
            <ClusterField
              id={"clusterName"}
              label={"Cluster Name"}
              labelValue={provisionRequest.clusterName}
              type={"label"}
            />
          </Grid>
          <p className={"headerValue"} style={{ paddingTop: 20, fontSize: 14 }}>
            Now that we have authorized your account for this cluster{" "}
            {provisionRequest.clusterName}, we can proceed with the cluster
            provisioning.
          </p>
          <Grid container spacing={2} sx={{ width: 600 }}>
            <Grid item xs={4}>
              <div className={"headerText"}>Ignore VPC validation</div>
            </Grid>
            <Grid item xs={8}>
              <Checkbox
                id={"ignoreValidation"}
                onChange={(e, checked) => {
                  handleChangeIngnoreValidation(checked);
                }}
              />
            </Grid>
            <ClusterField
              id={"region"}
              onChange={(e, value, reason) =>
                handleChangeData(
                  e,
                  getValueForAutocomplete(value),
                  reason,
                  "region"
                )
              }
              error={errors["region"] !== undefined}
              ddWidth={280}
              items={regions ? regions : []}
              label={"Region"}
              type={"select"}
              helperText={
                "Enter the region where you want to provision this cluster."
              }
            />
            <ClusterField
              id={"vpcId"}
              onChange={(e, value, reason) =>
                handleChangeData(
                  e,
                  getValueForAutocomplete(value),
                  reason,
                  "vpcId"
                )
              }
              ddWidth={280}
              error={errors["vpcId"] !== undefined}
              value={provisionRequest.vpcId}
              isOptionEqualToValue={(o: any, v: any) => {
                return o.id && o.id === v;
              }}
              getOptionLabel={(val: any) => {
                if (val.title) {
                  return val.title;
                }
                return val;
              }}
              items={vpcIds ? vpcIds : []}
              label={"VPC Id"}
              type={"select"}
              helperText={
                "Select a VPC (Available VPCs in your account will be loaded by selected region)"
              }
            />
          </Grid>

          {validateVpcAction.isLoading && (
            <Box
              className={"headerValue"}
              sx={{
                paddingTop: "20px",
                fontSize: "14px",
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
              }}
            >
              <Box sx={{ height: 50 }}>
                <CircularProgress />
              </Box>
              <Box sx={{ paddingLeft: 2 }}>Validating VPC settings...</Box>
            </Box>
          )}
          {!validateVpcAction.isLoading && vpcValidReason !== "" && (
            <div
              className={"headerValue"}
              style={{
                paddingTop: 20,
                fontSize: 14,
                display: "flex",
                justifyContent: "flex-start",
              }}
            >
              <div style={{ color: "#e30508" }}>{vpcValidReason}</div>
            </div>
          )}
          {!validateVpcAction.isLoading &&
            vpcValidReason === "" &&
            vpcInfo !== null && (
              <>
                <div
                  className={"headerValue"}
                  style={{
                    paddingTop: 20,
                    fontSize: 14,
                    display: "flex",
                    justifyContent: "flex-start",
                    paddingBottom: 20,
                  }}
                >
                  <div style={{ color: "#03ad31" }}>
                    Great - We have validated this VPC and it meets the
                    requirements for provisioning this Conductor cluster.
                  </div>
                </div>

                <Grid container spacing={2} sx={{ width: 600 }}>
                  <ClusterField
                    id={"azIds"}
                    onChange={(e, value, reason) =>
                      handleChangeData(
                        e,
                        getValueForAutocomplete(value),
                        reason,
                        "azIds"
                      )
                    }
                    onBlur={() => {
                      setIsAzFocused(false);
                    }}
                    onFocus={() => {
                      setIsAzFocused(true);
                    }}
                    error={errors["azIds"] !== undefined}
                    ddWidth={500}
                    disableCloseOnSelect={true}
                    items={availabilityZones}
                    value={selectedAzOptions}
                    getOptionDisabled={(option: any) =>
                      activeSku !== null &&
                      selectedAzOptions.length >= activeSku.azCount &&
                      !selectedAzOptions.some((zone) => zone.id === option.id)
                    }
                    label={"Preferred Availability Zones"}
                    type={"select"}
                    multiple={true}
                    helperText={
                      // Legacy text is left exactly as it was; only the SKU path explains its
                      // own AZ requirement and surfaces the mismatch inline.
                      activeSku
                        ? errors["azIds"] !== undefined
                          ? errors["azIds"]
                          : `${activeSku.skuName} requires exactly ${activeSku.azCount} availability zones.`
                        : `Let's pick your preferred availability zones. Max of 3.`
                    }
                  />
                  {/* A SKU states its own AZ count, so the generic 3-AZ nudge does not apply. */}
                  {!activeSku && !isAzFocused && azCount > 0 && azCount < 3 && (
                    <div
                      style={{
                        color: "#f0ad4e",
                        fontSize: 14,
                        padding: 16,
                        paddingRight: 0,
                      }}
                    >
                      We recommend choosing 3 availability zones to ensure the
                      highest possible reliability.
                    </div>
                  )}

                  <Grid container pl={2}>
                    <Grid
                      item
                      xs={4}
                      sx={{
                        fontSize: "14px",
                        fontFamily: "Lexend, sans-serif",
                        fontWeight: 600,
                        pt: 1.5,
                      }}
                    >
                      {"Domain Url"}
                    </Grid>
                    <Grid item xs={4} pl={0.6}>
                      <ClusterField
                        id={"subDomain"}
                        textSuffix={`.`}
                        value={subDomain}
                        onChange={onChangeSubDomain}
                        error={
                          selectedDomain &&
                          !selectedDomain?.fixedDomain &&
                          (!subDomain || errors.subDomain)
                        }
                        ddWidth={190}
                        label={""}
                        type={"text"}
                        helperText={
                          !selectedDomain?.fixedDomain && errors.subDomain
                            ? errors.subDomain
                            : selectedDomain?.fixedDomain
                            ? "For Orkes domains, sub domains will be auto assigned"
                            : "Sub domain"
                        }
                        disabled={selectedDomain?.fixedDomain}
                        placeholder={
                          selectedDomain?.fixedDomain ? "Auto Assigned" : ""
                        }
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <ClusterField
                        id={"domainUrl "}
                        value={selectedDomain}
                        onChange={onChangeBaseDomain}
                        error={errors["domainUrl"] !== undefined}
                        ddWidth={310}
                        items={route53Zones}
                        label={""}
                        type={"select"}
                        multiple={false}
                        helperText={
                          "Access to Conductor will be configured in this selected domain"
                        }
                      />
                    </Grid>
                  </Grid>

                  <ClusterField
                    id={"ingressType"}
                    onChange={(e, value, reason) =>
                      handleChangeData(
                        e,
                        getValueForAutocomplete(value),
                        reason,
                        "ingressType"
                      )
                    }
                    error={errors["ingressType"] !== undefined}
                    ddWidth={400}
                    items={ingressTypeItems}
                    label={"Ingress Type"}
                    type={"select"}
                    multiple={false}
                    helperText={
                      ingressTypeItems.length < ingressTypes.length
                        ? "Internet access is unavailable: no subnet in this VPC routes 0.0.0.0/0 to an internet gateway, so AWS cannot create an internet-facing load balancer."
                        : "Pick access to Conductor app and UI - via Internet or via VPC"
                    }
                  />
                  <Grid item xs={12} sx={{ mt: 1 }}>
                    <label
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <input
                        type="checkbox"
                        checked={enableApiGateway}
                        onChange={(e) => setEnableApiGateway(e.target.checked)}
                      />
                      <span>Enable API Gateway</span>
                    </label>
                  </Grid>
                  {skuModeAvailable && (
                    <Grid item xs={12} sx={{ mt: 1 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 16,
                        }}
                      >
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <input
                            type="radio"
                            name="sizingMode"
                            checked={sizingMode === "legacy"}
                            onChange={() => handleChangeSizingMode("legacy")}
                          />
                          <span>Cluster Capacity (legacy)</span>
                        </label>
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <input
                            type="radio"
                            name="sizingMode"
                            checked={sizingMode === "sku"}
                            onChange={() => handleChangeSizingMode("sku")}
                          />
                          <span>OCU SKU</span>
                        </label>
                      </div>
                    </Grid>
                  )}
                  {sizingMode === "sku" ? (
                    <ClusterField
                      id={"skuName"}
                      onChange={(e, value) => handleChangeSku(value ?? null)}
                      error={errors["skuName"] !== undefined}
                      ddWidth={500}
                      items={skuItems}
                      value={activeSku}
                      label={"Cluster SKU"}
                      type={"select"}
                      multiple={false}
                      helperText={
                        "Pick an OCU SKU. Infrastructure sizing and limits come from the SKU."
                      }
                    />
                  ) : (
                    <ClusterField
                      id={"computeUnits"}
                      onChange={(e, value, reason) =>
                        handleChangeData(
                          e,
                          getValueForAutocomplete(value),
                          reason,
                          "computeUnits"
                        )
                      }
                      error={errors["computeUnits"] !== undefined}
                      ddWidth={500}
                      value={
                        computeUnits.find(
                          (unit) =>
                            unit.id === String(provisionRequest.computeUnits)
                        ) ?? null
                      }
                      items={computeUnits}
                      label={"Cluster Capacity"}
                      type={"select"}
                      multiple={false}
                      helperText={
                        "Pick a suitable capacity for your cluster. This can be adjusted later."
                      }
                      showDropdownIcons={true}
                      iconHandler={getComputeUnitIcon}
                    />
                  )}
                  <ClusterField
                    id={"environmentType"}
                    onChange={(
                      e: ChangeEvent<HTMLInputElement>,
                      value: EnvironmentTypeOption
                    ) => {
                      setEnvironmentType(value);
                    }}
                    error={errors["environmentType"] !== undefined}
                    ddWidth={300}
                    items={environmentTypeOptions}
                    value={environmentType}
                    label={"Environment Type"}
                    type={"select"}
                    helperText={
                      "Tag your cluster's environment type. It is helpful for distinguishing between multiple instances."
                    }
                    showDropdownIcons={true}
                    iconHandler={getEnvironmentIcon}
                  />
                  <ClusterField
                    id={"enableAuthentication"}
                    onChange={(e, value, reason) =>
                      handleChangeData(
                        e,
                        getValueForAutocomplete(value),
                        reason,
                        "enableAuthentication"
                      )
                    }
                    error={errors["enableAuthentication"] !== undefined}
                    ddWidth={300}
                    defaultValue={authenticationTypes[0]}
                    items={authenticationTypes}
                    label={"Enable authentication"}
                    type={"select"}
                    multiple={false}
                    helperText={
                      "Set this to Yes to protect access to your Conductor cluster. A default authorization configuration will be added and you can change this to your own OAuth provider."
                    }
                  />
                  {/* Hidden for an empty VPC, which is the path where Orkes creates the
                      subnets: there is nothing to choose, and "exactly one subnet per
                      availability zone" is the wrong instruction when none exist yet. Still
                      shown when a VPC has subnets but none are usable, because seeing them
                      listed and unselected is how the customer works out why. */}
                  {vpcInfo !== null && vpcInfo.vpcSubnets.length > 0 && (
                    <ClusterField
                      id={"subnets"}
                      onChange={(e, value, reason) => setSubnetsSelected(value)}
                      error={errors["subnets"] !== undefined}
                      ddWidth={400}
                      items={subnetItems}
                      value={subnetSelected}
                      label={"Cluster subnets"}
                      type={"select"}
                      multiple={true}
                      helperText={
                        "Exactly one subnet per availability zone, across 2 or 3 zones. " +
                        "Defaulted to every subnet with a route to the internet; remove any " +
                        "you do not want the cluster placed in."
                      }
                    />
                  )}
                </Grid>
              </>
            )}

          <p className={"headerValue"} style={{ paddingTop: 20, fontSize: 14 }}>
            Once you have provided / selected all the required info, you can
            click the button below to start the cluster provisioning. Most
            provisioning flows will complete within an hour and we can monitor
            progress.
          </p>

          {byon && (
            <div
              className={"headerValue"}
              style={{ paddingBottom: 10, fontSize: 14, color: "#03ad31" }}
            >
              Customer-owned networking: Orkes will create no subnets, route
              tables or gateways in this VPC, and holds no permission to do so.
            </div>
          )}

          {createErrorReason !== "" && (
            <div
              className={"headerValue"}
              style={{ paddingBottom: 10, fontSize: 14, color: "#e30508" }}
            >
              {createErrorReason}
            </div>
          )}

          <StyledMainButton
            size="small"
            color={"inherit"}
            style={{
              width: 250,
              fontSize: 15,
              marginRight: 10,
              marginTop: 10,
            }}
            onClick={() => {
              triggerProvisioningFlow(false);
            }}
          >
            Create Provisioning Workflow
          </StyledMainButton>
        </Paper>
        <Box style={{ minHeight: 100 }}>
          <p />
        </Box>
      </Box>
    </>
  );
}
